import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { aimJet, predictShot, walkingWith, type ShotAim } from "../sim/backpack";
import { PAINT_COOLDOWN } from "../sim/tuning";
import type { Skater } from "../skater";

// The paintball gun's crosshair (#143), render only: with the gun out on
// foot, a reticle sits where the next shot would land, on the first surface
// its arc hits (`predictShot` flies the shot the sim would fire, auto-aim
// and all, without firing it). It faces the camera and keeps one size on
// screen however far off it lands. It turns red when the auto-aim has a target,
// shrinks while the gun cools down and hides when the gun is put away. It
// follows where you face, so touch needs no mouse. Plain unlit and drawn
// over everything, so it reads the same in every art style. With the power
// washer out (#151) it sits where the jet hits (`aimJet`).

/** The reticle's radius per metre from the camera (about 1.4° across its radius, whatever the distance). */
const SCREEN_RADIUS = 0.025;
/** How much smaller it is just after a shot (it eases back to full size over the cooldown). */
const COOLDOWN_SHRINK = 0.4;
/** How far toward the camera from the spot it sits (it draws over everything; this keeps it off the surface for sorting). */
const LIFT = 0.05;
const FREE_COLOR = 0xffffff;
const LOCK_COLOR = 0xff3b3b;

export class Crosshair {
  readonly root = new THREE.Group();
  /** The last prediction, or null with the gun away or a shot that flies off (for tests). */
  aim: ShotAim | null = null;
  private mat: THREE.MeshBasicMaterial;
  private v = new THREE.Vector3();

  constructor(private step: number) {
    this.root.name = "crosshair";
    this.mat = new THREE.MeshBasicMaterial({
      color: FREE_COLOR,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    // A ring with four ticks pointing in, and a dot: unit radius, facing +z (the camera), one draw call.
    const parts: THREE.BufferGeometry[] = [new THREE.RingGeometry(0.82, 1, 32), new THREE.CircleGeometry(0.12, 12)];
    for (let i = 0; i < 4; i++) {
      const g = new THREE.PlaneGeometry(0.1, 0.38);
      g.translate(0, 0.66, 0);
      g.rotateZ((i * Math.PI) / 2);
      parts.push(g);
    }
    const mesh = new THREE.Mesh(mergeGeometries(parts), this.mat);
    for (const g of parts) g.dispose();
    mesh.renderOrder = 10;
    mesh.frustumCulled = false;
    this.root.add(mesh);
    this.root.visible = false;
  }

  /** Puts the reticle where a shot fired now would land, or hides it. */
  update(sk: Skater, camera: THREE.Camera): void {
    const out = walkingWith(sk);
    this.aim = out === "paintball" ? predictShot(sk, this.step) : out === "washer" ? jetAim(sk) : null;
    const a = this.aim;
    this.root.visible = a !== null;
    if (!a) return;
    const toCam = this.v.copy(camera.position).sub(this.root.position.set(a.x, a.y, a.z));
    const dist = toCam.length();
    this.root.position.addScaledVector(toCam, LIFT / Math.max(dist, 1e-3));
    this.root.quaternion.copy(camera.quaternion);
    const cool = 1 - COOLDOWN_SHRINK * Math.min(1, sk.pack.cooldown / PAINT_COOLDOWN);
    this.root.scale.setScalar(SCREEN_RADIUS * dist * cool);
    this.mat.color.setHex(a.aim !== null ? LOCK_COLOR : FREE_COLOR);
  }

  dispose(): void {
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    this.mat.dispose();
  }
}

/** Where the washer's jet would hit, as an aim with no target, or null if it hits nothing in reach. */
function jetAim(sk: Skater): ShotAim | null {
  const hit = aimJet(sk).hit;
  return hit ? { ...hit, aim: null, target: null } : null;
}
